import type { ContactMethod, DeliveryAddressModel, DeliveryAddressReqModel, PaymentAddressModel, PaymentAddressReqModel, UserDetailsModel, UserUnsubscribeFromEpaperModel } from '@develop/ia-api-library'
import {
  addDeliveryAddress as addDeliveryAddressAPI,
  addPaymentAddressV2 as addPaymentAddressAPI,
  changeSelectedPharmacy as changeSelectedPharmacyAPI,
  crmConfirmLinkExistingUser as crmConfirmLinkExistingUserAPI,
  crmLinkPharmacy as crmLinkPharmacyAPI,
  crmListPharmacies as crmListPharmaciesAPI,
  crmRemovePharmacy as crmRemovePharmacyAPI,
  crmUnlinkPharmacy as crmUnlinkPharmacyAPI,
  editUserDetails as editUserDetailsAPI,
  listDeliveryAddresses as getDeliveryAddressesAPI,
  listPaymentAddressesV2 as getPaymentAddressesAPI,
  getUserDetails,
  unsubscribeFromEpaper as unsubscribeFromEpaperAPI,
} from '@develop/ia-api-library'
import { getDateDaysFromNow } from '@develop/ia-utils'
import { StorageSerializers } from '@vueuse/core'
import { defineStore } from 'pinia'
import type { UserDetailsReduced } from '@/models/user'
import type { CookieOptions } from '@/models/cookie'
import { LocalStorage } from '~/models'
import { IaCookie } from '~/models/cookie'

// TODO: should be a composable
export const useUserStore = defineStore('user', () => {
  const { fecApiKey } = useEnvs()
  const { locale } = useTranslation()

  const showUnverifiedModal = useLocalStorage<boolean>(LocalStorage.UserUnverified, false, { initOnMounted: true })
  const hasUserResetPassword = useSessionStorage<boolean>(LocalStorage.PasswordReset, false, { initOnMounted: true })
  const updateUserAddressMessage = useLocalStorage<string | undefined>(LocalStorage.UpdateUserAddressMessage, undefined, { initOnMounted: true })

  const setUpdateUserAddressMessage = (message?: string) => set(updateUserAddressMessage, message)
  const setShowUnverifiedModal = (show: boolean) => set(showUnverifiedModal, show)
  const setHasUserResetPassword = (hasReset: boolean) => set(hasUserResetPassword, hasReset)

  const authStore = useAuthStore()
  const { tokens, isAuthenticated } = storeToRefs(authStore)

  const { deleteCookie, setCookie, getCookie } = useCookieManagerStore()

  const userLS = useLocalStorage<UserDetailsModel | undefined>(IaCookie.UserData, undefined, {
    serializer: StorageSerializers.object,
    initOnMounted: true,
    writeDefaults: false,
  })

  // TODO: useAsyncData shouldn't be called in a store, this entire store should be turned into a composable
  // gets called manually from authStore when setting/deleting cookies, so that we can await the login process
  const { data: userRes, refresh: fetchUser, error } = useAsyncData('user', async () => {
    if (isAuthenticated.value) {
      const res = await getUserDetails(tokens.value!)

      if (!res.success)
        throw new Error('Error fetching user details')

      return res
    }
    return null
  }, {
    immediate: Boolean(isAuthenticated.value),
  })

  const user = computed(() => userRes.value?.data)

  // sync local storage just in case, we do not use it though
  syncRefs(user, userLS)

  watch(error, (val) => {
    onShowLogs(() => console.error('🚧 user fetch error :', val))
    performLogout({ withoutConfirmation: true })
  })

  const isLoading = useState('user:store:loading', () => false)

  const fullName = computed(() => user.value ? `${user.value.firstName} ${user.value.lastName}` : '')
  const fullNameWithSalutation = computed(() => {
    if (!user.value)
      return ''

    const salutation = user.value.salutation
    const salutationPreview = ['Herr', 'Frau'].includes(salutation) ? salutation : ''
    return `${salutationPreview} ${fullName.value}`
  })
  const userId = computed(() => user.value?.id)

  const mobileNumber = computed(() => user.value ? `+${user.value?.mobileCountryCode} ${user.value?.mobileNumberWithoutCountryCode}` : '')
  const phoneNumber = computed(() => user.value ? `+${user.value?.phoneCountryCode} ${user.value?.phoneNumberWithoutCountryCode}` : '')
  const userPharmacyId = computed(() => user.value?.pharmacyId)
  const isLoggedIn = computed(() => userId.value !== undefined && authStore.isAuthenticated)
  const isEpaperSubscribed = computed(() => user.value?.epaperSubscribed)

  const $userDetailsReduced = computed(() => {
    if (user.value) {
      const { firstName, lastName, id, isAccountOlderThan24hours, isEmailConfirmed } = user.value
      return { firstName, lastName, id, isAccountOlderThan24hours, isEmailConfirmed } as UserDetailsReduced
    }
    return undefined
  })

  const userDetailsReduced = useCookie(IaCookie.UserData, {
    expires: getDateDaysFromNow(30),
    // TODO: does not update automatically, but it should (when passed () => Ref<T>)
    // perhaps bcs it is inside a store instead of composable?
    default: () => $userDetailsReduced.value,
  })

  syncRefs($userDetailsReduced, userDetailsReduced, { deep: true, immediate: false })

  // TODO: having fetch and loadUser is confusing, move this into the useAsyncData itself?
  const loadUser = async () => {
    const response = await fetchUser()
    if (response) {
      // when do we need interceptedData (i forgot)
      const { data, interceptedData } = response
      if (interceptedData?.refreshAuthModel)
        authStore.login(interceptedData.refreshAuthModel)

      return data
    }
  }

  // Selected pharmacy
  const updateUserPharmacy = async (pharmacyId: number, options?: { refetchUser?: boolean }) => {
    if (isAuthenticated.value) {
      const { success } = await changeSelectedPharmacyAPI({ pharmacyId }, tokens.value!)

      if (success && options?.refetchUser)
        await loadUser()
    }
  }

  // TODO: replace with useCookie
  const getPharmacyCookie = () => getCookie<number | undefined>(IaCookie.SelectedPharmacy) ?? undefined
  const setPharmacyCookie = (id: number, options?: CookieOptions) => setCookie({ name: IaCookie.SelectedPharmacy, value: id, options })
  const removePharmacyCookie = () => deleteCookie(IaCookie.SelectedPharmacy)

  const getDeliveryAddresses = async () => {
    if (isAuthenticated.value) {
      isLoading.value = true
      const response = await getDeliveryAddressesAPI(tokens.value!)
      if (response.success) {
        isLoading.value = false
        // Data unnecessarily wrapped in data in response
        return response.data?.data
      }

      onShowLogs(() => console.error(response.message))
      isLoading.value = false
    }
    return []
  }

  const getDeliveryAddressById = async (id: number) => {
    if (!user.value)
      return null

    if (isAuthenticated.value) {
      isLoading.value = true
      const response = await getDeliveryAddressesAPI(tokens.value!)
      if (response.success) {
        isLoading.value = false
        return response.data?.data.find((address: DeliveryAddressModel) => address.id === id)
      }

      onShowLogs(() => console.error(response.message))
      isLoading.value = false
    }
    return null
  }

  const addNewDeliveryAddress = async (address: DeliveryAddressReqModel) => {
    if (!user.value)
      return null

    if (isAuthenticated.value) {
      isLoading.value = true
      const response = await addDeliveryAddressAPI(address, tokens.value!)
      if (response.success) {
        isLoading.value = false
        return response.data?.id
      }

      onShowLogs(() => console.error(response.message))
      isLoading.value = false
      return null
    }
  }

  const getPaymentAddresses = async () => {
    if (!user.value)
      return []

    if (isAuthenticated.value) {
      isLoading.value = true
      const response = await getPaymentAddressesAPI(tokens.value!)
      if (response.success) {
        isLoading.value = false
        // Data unnecessarily wrapped in data in response - backend issue
        return response.data?.data || []
      }

      onShowLogs(() => console.error(response.message))
      isLoading.value = false
    }
    return []
  }

  const getPaymentAddressById = async (id: number) => {
    if (!user.value)
      return null

    if (isAuthenticated.value) {
      isLoading.value = true
      const response = await getPaymentAddressesAPI(tokens.value!)
      if (response.success) {
        isLoading.value = false
        return response.data?.data.find((address: PaymentAddressModel) => address.id === id)
      }

      onShowLogs(() => console.error(response.message))
      isLoading.value = false
    }
    return null
  }

  const addNewPaymentAddress = async (address: PaymentAddressReqModel) => {
    if (!user.value)
      return

    if (isAuthenticated.value) {
      isLoading.value = true
      const response = await addPaymentAddressAPI(address, tokens.value!)
      if (response.success) {
        isLoading.value = false
        return response.data
      }

      onShowLogs(() => console.error(response.message))
      isLoading.value = false
      return null
    }
  }

  const updatePreferredWayOfContact = async (preferredWayOfContact: ContactMethod) => {
    if (!user.value)
      return

    const { token, refreshToken } = authStore

    const { salutation, firstName, lastName, email, phone, phoneCountryCode, phoneNumberWithoutCountryCode, mobile, mobileCountryCode, mobileNumberWithoutCountryCode, streetName, houseNumber, zipCode, cityName } = user.value
    const initialValues = {
      salutation,
      firstName,
      lastName,
      email,
      phone,
      phoneCountryCode: phoneCountryCode ?? 0,
      phoneNumberWithoutCountryCode: phoneNumberWithoutCountryCode ?? '',
      mobile,
      mobileCountryCode: mobileCountryCode ?? 0,
      mobileNumberWithoutCountryCode: mobileNumberWithoutCountryCode ?? '',
      streetName,
      houseNumber,
      zipCode,
      cityName,
    }
    isLoading.value = true
    await editUserDetailsAPI(
      {
        ...initialValues,
        preferredWayOfContact,
      },
      ({
        token,
        refreshToken,
      }))
    await loadUser()
    isLoading.value = false
  }

  const fetchCrmPharmacies = async () => {
    const options = {
      token: tokens.value?.token,
      refreshToken: tokens.value?.refreshToken,
      language: locale,
    }

    const { success, data } = await crmListPharmaciesAPI(options)
    return success ? data : []
  }

  const toggleCrmPharmacyLink = async (hasConsent: boolean, pharmacyId: number) => {
    const options = {
      token: tokens.value?.token,
      refreshToken: tokens.value?.refreshToken,
      language: locale,
    }

    const linkingActionApi = hasConsent ? crmLinkPharmacyAPI : crmUnlinkPharmacyAPI
    await linkingActionApi(pharmacyId.toString(), options)
  }

  const removeCrmPharmacy = async (pharmacyId: number) => {
    const options = {
      token: tokens.value?.token,
      refreshToken: tokens.value?.refreshToken,
      language: locale,
    }

    const { success } = await crmRemovePharmacyAPI(pharmacyId.toString(), options)
    return success
  }

  const confirmCrmLinkExistingUser = async (customerNumber: string, pharmacyId: number) => {
    const options = {
      token: tokens.value?.token,
      refreshToken: tokens.value?.refreshToken,
      language: locale,
    }
    const { success } = await crmConfirmLinkExistingUserAPI(customerNumber, pharmacyId.toString(), options)
    return success
  }

  const unsubscribeFromEpaper = async ({ userId, magazineTypeId }: UserUnsubscribeFromEpaperModel) => {
    if (!user.value)
      return

    if (isAuthenticated.value && fecApiKey.value) {
      isLoading.value = true
      const response = await unsubscribeFromEpaperAPI({ userId, magazineTypeId }, tokens.value!)
      isLoading.value = false

      if (response.success) {
        loadUser()
        return response.data
      }

      onShowLogs(() => console.error(response.message))
    }
  }

  // Flag used to track if a product page redirect occurs due to rating
  // Case 1: The user is not logged in, does not have a selected pharmacy in the URL, and after logging in, the user is redirected to the product page of their previously selected pharmacy
  // Case 2: The user is not logged in but has a pharmacy in the URL that is not their selected pharmacy. This redirect occurs if the user selects their previously chosen pharmacy (not the one in the URL) in the pharmacy switch modal
  const isProductRatingRedirectInProgress = ref(false)
  const setIsProductRatingRedirectInProgress = (value: boolean) => isProductRatingRedirectInProgress.value = value

  return {
    user,
    userDetailsReduced,
    fullName,
    fullNameWithSalutation,
    mobileNumber,
    phoneNumber,
    userId,
    userPharmacyId,
    hasUserResetPassword,
    updateUserAddressMessage,
    setHasUserResetPassword,
    loadUser,
    fetchUser,
    getDeliveryAddresses,
    getDeliveryAddressById,
    addNewDeliveryAddress,
    getPaymentAddresses,
    getPaymentAddressById,
    addNewPaymentAddress,
    updateUserPharmacy,
    setPharmacyCookie,
    getPharmacyCookie,
    removePharmacyCookie,
    setShowUnverifiedModal,
    setUpdateUserAddressMessage,
    showUnverifiedModal,
    unsubscribeFromEpaper,
    isLoading,
    isLoggedIn,
    isEpaperSubscribed,
    isProductRatingRedirectInProgress,
    setIsProductRatingRedirectInProgress,
    updatePreferredWayOfContact,
    fetchCrmPharmacies,
    toggleCrmPharmacyLink,
    removeCrmPharmacy,
    confirmCrmLinkExistingUser,
  }
})
